import { FtueVideo } from '../../../../api/core'
import { formatSeconds } from '../../../../features/ftue-videos/components/FtueVideoPlayer/formatSeconds'
import { chaptersMatch, toChapterBodies, translationsMatch } from '../../../components/FtueChapterEditor/ftueChapterHelpers'
import { thumbnailsMatch, toVimeoThumbnails, VimeoOEmbed } from '../../../helpers/vimeo'
import { ONBOARDING_VIDEO_TYPE, OnboardingVideoCreateBody, OnboardingVideoFormValues, OnboardingVideoUpdateBody } from '../types/OnboardingVideo'

export const formatVideoDuration = (seconds: number) => formatSeconds(seconds)

export const sortOnboardingVideos = (videos: FtueVideo[]) => [...videos].sort((a, b) => a.createdAt - b.createdAt || a.id.localeCompare(b.id))

export const findVideoWithVimeoId = (videos: FtueVideo[] | undefined, vimeoId: string) => videos?.find((video) => video.vimeoId === vimeoId)

export const getListThumbnail = (video: FtueVideo) => video.thumbnails?.[2] ?? video.thumbnails?.[0]

export const toOnboardingVideoCreateBody = (oembed: VimeoOEmbed): OnboardingVideoCreateBody => ({
  vimeoId: oembed.vimeoId,
  chapters: [],
  thumbnails: toVimeoThumbnails(oembed.thumbnailUrl),
  type: ONBOARDING_VIDEO_TYPE,
  names: { en: oembed.title },
})

export const toOnboardingVideoFormValues = (video?: FtueVideo | null): OnboardingVideoFormValues => ({
  names: video?.names ?? {},
  videoName: video?.videoName ?? '',
  thumbnails: video?.thumbnails ?? [],
  chapters: video?.chapters ?? [],
})

/** Legacy's update: the whole loaded video with the edits, `gameVersion` and `marketIso` set to `''`. */
export const toOnboardingVideoUpdateBody = (video: FtueVideo, values: OnboardingVideoFormValues): OnboardingVideoUpdateBody => ({
  ...video,
  names: values.names,
  videoName: values.videoName === '' && video.videoName === null ? null : values.videoName,
  thumbnails: values.thumbnails,
  chapters: toChapterBodies(values.chapters),
  gameVersion: '',
  marketIso: '',
})

export type OnboardingVideoField = 'vimeoId' | 'names' | 'videoName' | 'thumbnails' | 'chapters'

export const onboardingVideoFieldLabelKeys: { [field in OnboardingVideoField]: string } = {
  vimeoId: 'internal-csm:onboarding_videos_vimeo_id',
  names: 'internal-csm:onboarding_videos_title',
  videoName: 'internal-csm:onboarding_videos_saas_link',
  thumbnails: 'internal-csm:onboarding_videos_thumbnail',
  chapters: 'internal-common:ftue_chapters',
}

/** The sent values the stored video doesn't have, so a 2xx that didn't store everything is reported. */
export const getUnstoredOnboardingVideoFields = (
  sent: OnboardingVideoCreateBody | OnboardingVideoUpdateBody,
  stored?: FtueVideo | null
): OnboardingVideoField[] => {
  if (!stored) return ['vimeoId', 'names', 'thumbnails', 'chapters']
  const unstored: OnboardingVideoField[] = []
  if (sent.vimeoId !== stored.vimeoId) unstored.push('vimeoId')
  if (!translationsMatch(sent.names, stored.names)) unstored.push('names')
  if ('videoName' in sent && (sent.videoName ?? '') !== (stored.videoName ?? '')) unstored.push('videoName')
  if (!thumbnailsMatch(sent.thumbnails, stored.thumbnails)) unstored.push('thumbnails')
  if (!chaptersMatch(sent.chapters, stored.chapters ?? [])) unstored.push('chapters')
  return unstored
}
